import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, LinkedinIcon, MailIcon, MapPinIcon, PhoneIcon, SendIcon } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { profile } from '../data/site';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const inquiryTypes = ['Executive role (CLO / General Counsel)', 'Board or advisory engagement', 'Governance or compliance consulting', 'Speaking or media', 'Other'];
export function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    inquiry: inquiryTypes[0],
    message: ''
  });
  const update = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(prev => ({
    ...prev,
    [field]: event.target.value
  }));
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 1100);
  };
  const inputClass = 'w-full border border-navy/15 bg-white px-4 py-3.5 text-sm text-charcoal transition-colors duration-300 placeholder:text-charcoal/35 focus:border-royal focus:outline-none';
  return <main data-id="element-301" data-mp="mhchpplduqcj">
      <section className="boardroom-light relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-24" data-id="element-302" data-mp="mhw3jhe5lyvu">
        <div className="aurora" aria-hidden="true" data-id="element-303" data-mp="qixs94ag2sjk" />
        <div className="pattern-lattice-dark absolute inset-0 opacity-50" aria-hidden="true" data-id="element-304" data-mp="vh93njge1b3w" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10" data-id="element-305" data-mp="10gc8p3wdbhbv">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-306" data-mp="10hnd95xn0t41">
            <div className="flex items-center gap-3" data-id="element-307" data-mp="2btxviier96u8">
              <span className="h-px w-10 bg-gold" aria-hidden="true" data-id="element-308" data-mp="1m73o1wx3hkdd" />
              <p className="eyebrow text-gold" data-id="element-309" data-mp="1m96l30wo0drl">Connect</p>
            </div>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-light leading-[1.1] text-white sm:text-5xl lg:text-[3.5rem]" data-id="element-310" data-mp="1h7qwwzz6xr4z">
              Begin a confidential conversation.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300/90" data-id="element-311" data-mp="1r6zo5l2o8rnn">
              Inquiries from boards, CEOs, private equity firms, investors, and executive recruiters are
              welcome. All correspondence is treated as confidential.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-id="element-312" data-mp="1d8i7u8f3ihfr">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-10" data-id="element-313" data-mp="18exe4skjc9md">
          <Reveal data-id="element-314" data-mp="kp356m3c757e">
            <div className="border border-navy/10 bg-mist p-8 sm:p-10" data-id="element-315" data-mp="kojmh35v4csb">
              {status === 'success' ? <motion.div initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="flex min-h-[24rem] flex-col items-center justify-center text-center" role="status" data-id="element-316" data-mp="fp700jiwl7q9">
                  <CheckCircle2Icon className="h-12 w-12 text-gold" aria-hidden="true" data-id="element-317" data-mp="2mfac7jalsxqf" />
                  <h2 className="mt-6 font-display text-2xl font-light text-navy" data-id="element-318" data-mp="3a54nvxj6sg93">Message received</h2>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-charcoal/70" data-id="element-319" data-mp="3f47foga5opz4">
                    Thank you, {form.name.split(' ')[0] || 'and'} — your inquiry has been sent. You can
                    expect a reply within two business days.
                  </p>
                  <button type="button" onClick={() => {
                setStatus('idle');
                setForm({
                  name: '',
                  email: '',
                  organization: '',
                  inquiry: inquiryTypes[0],
                  message: ''
                });
              }} className="mt-8 text-[0.7rem] uppercase tracking-micro text-royal transition-colors hover:text-gold" data-id="element-320" data-mp="3584g9i6ov5fv">
                    Send another message
                  </button>
                </motion.div> : <form onSubmit={handleSubmit} noValidate data-id="element-321" data-mp="1v9ejeck8r025">
                  <h2 className="font-display text-2xl font-light text-navy" data-id="element-322" data-mp="1vaxxbwifcz0r">Send an inquiry</h2>

                  <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-323" data-mp="2sw3qh0tsw2uv">
                    <div data-id="element-324" data-mp="2nyts2vofoguw">
                      <label htmlFor="name" className="eyebrow block text-[0.6rem] text-charcoal/60" data-id="element-325" data-mp="2nylf73sb9bjp">
                        Name *
                      </label>
                      <input id="name" value={form.name} onChange={update('name')} className={`mt-2.5 ${inputClass}`} placeholder="Full name" required data-id="element-326" data-mp="2exvky4ip7t83" />
                    </div>
                    <div data-id="element-327" data-mp="3f750s11ctd24">
                      <label htmlFor="email" className="eyebrow block text-[0.6rem] text-charcoal/60" data-id="element-328" data-mp="3j6vry5uslv76">
                        Email *
                      </label>
                      <input id="email" type="email" value={form.email} onChange={update('email')} className={`mt-2.5 ${inputClass}`} placeholder="name@company.com" required data-id="element-329" data-mp="3o4ns1n5lcnmg" />
                    </div>
                  </div>

                  <div className="mt-5" data-id="element-330" data-mp="aozoxgyip8q5">
                    <label htmlFor="organization" className="eyebrow block text-[0.6rem] text-charcoal/60" data-id="element-331" data-mp="fqf6gz1hkx3s">
                      Organization
                    </label>
                    <input id="organization" value={form.organization} onChange={update('organization')} className={`mt-2.5 ${inputClass}`} placeholder="Company, fund, or firm" data-id="element-332" data-mp="2hhjyhw7bzzon" />
                  </div>

                  <div className="mt-5" data-id="element-333" data-mp="3ttdyqcf3ox5u">
                    <label htmlFor="inquiry" className="eyebrow block text-[0.6rem] text-charcoal/60" data-id="element-334" data-mp="3tr19rfaxdmja">
                      Nature of inquiry
                    </label>
                    <select id="inquiry" value={form.inquiry} onChange={update('inquiry')} className={`mt-2.5 ${inputClass}`} data-id="element-335" data-mp="7kmk43gxzfhj">
                      {inquiryTypes.map(type => <option key={type} value={type} data-id="element-336" data-mp="cdp69qje3mnp">
                          {type}
                        </option>)}
                    </select>
                  </div>

                  <div className="mt-5" data-id="element-337" data-mp="ledhyelb9axf">
                    <label htmlFor="message" className="eyebrow block text-[0.6rem] text-charcoal/60" data-id="element-338" data-mp="lb7wd9bw4r6g">
                      Message *
                    </label>
                    <textarea id="message" rows={5} value={form.message} onChange={update('message')} className={`mt-2.5 resize-none ${inputClass}`} placeholder="Briefly describe the opportunity or question." required data-id="element-339" data-mp="19ww2ypb9erxq" />
                  </div>

                  {status === 'error' ? <p role="alert" className="mt-5 border-l-2 border-royal bg-white px-4 py-3 text-sm text-royal" data-id="element-340" data-mp="caozjiqoz5my">
                      Please complete your name, email, and message before sending.
                    </p> : null}

                  <button type="submit" disabled={status === 'submitting'} className="btn-lux group mt-8 inline-flex items-center gap-2.5 bg-royal px-9 py-4 text-[0.7rem] font-medium uppercase tracking-micro text-white transition-colors duration-300 hover:text-navy disabled:cursor-not-allowed disabled:opacity-70" data-id="element-341" data-mp="3aaaq9eir8ep">
                    {status === 'submitting' ? 'Sending…' : 'Send message'}
                    <SendIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-342" data-mp="8skf54ir10uo" />
                  </button>
                </form>}
            </div>
          </Reveal>

          <Reveal delay={0.1} data-id="element-343" data-mp="ikjmvd2esb">
            <div className="space-y-6" data-id="element-344" data-mp="3w4tscagp8h1o">
              <a href={profile.linkedin} target="_blank" rel="noreferrer" className="btn-lux group flex items-center justify-between border border-navy/15 bg-navy px-7 py-6 text-white transition-colors duration-300 hover:text-navy" data-id="element-345" data-mp="3r6sblw6qwttd">
                <span data-id="element-346" data-mp="3d8vcot4tkbbj">
                  <span className="eyebrow block text-[0.6rem] text-gold transition-colors group-hover:text-navy/70" data-id="element-347" data-mp="3d8lvnv1r5ehz">
                    Professional network
                  </span>
                  <span className="mt-1.5 block font-display text-lg font-normal" data-id="element-348" data-mp="1wq1xtqn0lcdf">Connect on LinkedIn</span>
                </span>
                <LinkedinIcon className="h-5 w-5" aria-hidden="true" data-id="element-349" data-mp="21odcqfs62d2i" />
              </a>

              <a href={`mailto:${profile.email}`} className="group flex items-center justify-between border border-navy/15 bg-mist px-7 py-6 transition-all duration-300 hover:border-violet hover:shadow-jewel" data-id="element-350" data-mp="26orghlmqj0uq">
                <span data-id="element-351" data-mp="1izophn67nvnk">
                  <span className="eyebrow block text-[0.6rem] text-violet" data-id="element-352" data-mp="1iydplyfvz81q">Email</span>
                  <span className="mt-1.5 block font-display text-lg font-normal text-navy" data-id="element-353" data-mp="1nrglid84ytl3">
                    {profile.email}
                  </span>
                </span>
                <MailIcon className="h-5 w-5 text-navy transition-colors group-hover:text-violet" aria-hidden="true" data-id="element-354" data-mp="v9ujnpkb77ct" />
              </a>

              <a href={`tel:${profile.phoneHref}`} className="group flex items-center justify-between border border-navy/15 bg-mist px-7 py-6 transition-all duration-300 hover:border-teal hover:shadow-lux" data-id="element-355" data-mp="twf84mbr6tke">
                <span data-id="element-356" data-mp="13u3bg0nfd9e6">
                  <span className="eyebrow block text-[0.6rem] text-teal" data-id="element-357" data-mp="13sifc6lgpie7">Direct line</span>
                  <span className="mt-1.5 block font-display text-lg font-normal text-navy" data-id="element-358" data-mp="659p5yainl5v">
                    {profile.phone}
                  </span>
                </span>
                <PhoneIcon className="h-5 w-5 text-navy transition-colors group-hover:text-teal" aria-hidden="true" data-id="element-359" data-mp="g3pfhz1pyqeb" />
              </a>

              <div className="border border-navy/15 bg-mist px-7 py-6" data-id="element-360" data-mp="3jl7m635r9rd5">
                <span className="eyebrow block text-[0.6rem] text-royal" data-id="element-361" data-mp="3okak73nbixbm">Location</span>
                <span className="mt-1.5 flex items-center gap-2 font-display text-lg font-normal text-navy" data-id="element-362" data-mp="25qhge2fpvnok">
                  <MapPinIcon className="h-4 w-4 text-garnet" aria-hidden="true" data-id="element-363" data-mp="21o4b1f4vtrsb" />
                  {profile.location}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/65" data-id="element-364" data-mp="1wngc87aew6nj">
                  Serving clients and boards nationally, with regulatory experience across all 50 states.
                </p>
              </div>

              <div className="pattern-lattice border border-navy/15 px-7 py-6" data-id="element-365" data-mp="1i0erfv73mxu6">
                <p className="text-sm leading-relaxed text-charcoal/75" data-id="element-366" data-mp="1hyacmi7nwef4">
                  For executive search and board opportunities, please include the mandate, reporting line,
                  and timeline in your note.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>;
}